import type { RedshiftExportJob } from '@atlas/shared';
import { listRedshiftExports } from './callables.js';
import { poll, type Unsubscribe } from './poll.js';

/** How many runs the table shows. Old runs stay readable via the console. */
export const PAGE_SIZE = 50;

/**
 * Poll every Redshift export, newest first. Any allowed user may read (the
 * listRedshiftExports callable enforces it; mutations happen only via
 * callables), so this is a shared view like Imports and Exports.
 */
export function subscribeToRedshiftExports(
  onUpdate: (exports: Array<RedshiftExportJob & { id: string }>) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  return poll(
    async () => (await listRedshiftExports({ limit: PAGE_SIZE })).data.exports,
    onUpdate,
    onError,
  );
}

/** Human-readable byte size, e.g. "1.4 GB". */
export function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(
    units.length - 1,
    Math.floor(Math.log(bytes) / Math.log(1024)),
  );
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

/** Row counts from SVV_TABLE_INFO are approximate and can be missing. */
export function formatRows(rows: number | null): string {
  if (rows === null || rows < 0) return '—';
  return rows.toLocaleString('en-GB');
}
